import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  useRecurringSchedules,
  useAcknowledgeRecurringSchedule,
} from '@/hooks/useRecurringSchedules';
import { useDuplicateJob, useJob } from '@/hooks/useJobs';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RecurringScheduleModal } from './RecurringScheduleModal';
import type { RecurringJobScheduleSummary } from '@jobos/shared';

const intervalLabel: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

function formatDate(value: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}

export default function RecurringList() {
  const { data: schedules, isLoading } = useRecurringSchedules();
  const [editing, setEditing] = useState<RecurringJobScheduleSummary | null>(null);

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Recurring Reminders</h1>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : !schedules || schedules.length === 0 ? (
        <p className="text-gray-500 py-12 text-center">
          No recurring reminders yet. Open a job and use "Set Up Recurring Reminder" in the Actions panel.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Interval</TableHead>
              <TableHead>Next due</TableHead>
              <TableHead>Last fired</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedules.map((s) => (
              <ScheduleRow key={s.id} schedule={s} onEdit={() => setEditing(s)} />
            ))}
          </TableBody>
        </Table>
      )}

      {editing && (
        <RecurringScheduleModal
          open={!!editing}
          onOpenChange={(open) => !open && setEditing(null)}
          sourceJobId={editing.sourceJobId}
          existing={editing}
        />
      )}
    </div>
  );
}

function ScheduleRow({
  schedule,
  onEdit,
}: {
  schedule: RecurringJobScheduleSummary;
  onEdit: () => void;
}) {
  const navigate = useNavigate();
  const duplicateMut = useDuplicateJob();
  const ackMut = useAcknowledgeRecurringSchedule();
  const dismissMut = useAcknowledgeRecurringSchedule();
  const { data: sourceJob } = useJob(schedule.sourceJobId);

  const handleDuplicate = async () => {
    if (!sourceJob) return;
    const newJob = await duplicateMut.mutateAsync(sourceJob);
    await ackMut.mutateAsync(schedule.id);
    navigate(`/jobs/${newJob.id}`);
  };

  const handleDismiss = async () => {
    if (!confirm('Dismiss this reminder? It will advance to the next cycle without duplicating the job.')) return;
    await dismissMut.mutateAsync(schedule.id);
  };

  return (
    <TableRow className={schedule.isDue ? 'bg-amber-50' : ''}>
      <TableCell>
        <a className="text-blue-600 hover:underline" href={`/jobs/${schedule.sourceJobId}`}>
          {schedule.sourceJob.jobCode ?? schedule.sourceJob.id.slice(0, 8)}
        </a>
        <div className="text-xs text-gray-500">{schedule.sourceJob.description ?? '—'}</div>
      </TableCell>
      <TableCell>{schedule.sourceJob.clientName ?? '—'}</TableCell>
      <TableCell>{intervalLabel[schedule.interval] ?? schedule.interval}</TableCell>
      <TableCell>{formatDate(schedule.nextDueAt)}</TableCell>
      <TableCell>{formatDate(schedule.lastFiredAt)}</TableCell>
      <TableCell className="text-right space-x-2">
        {schedule.isDue && (
          <>
            <Button
              size="sm"
              onClick={handleDuplicate}
              disabled={duplicateMut.isPending || ackMut.isPending || !sourceJob}
            >
              {duplicateMut.isPending || ackMut.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  Duplicating...
                </>
              ) : (
                'Duplicate now'
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDismiss}
              disabled={dismissMut.isPending}
            >
              {dismissMut.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  Dismissing...
                </>
              ) : (
                'Dismiss'
              )}
            </Button>
          </>
        )}
        <Button size="sm" variant="secondary" onClick={onEdit}>
          Edit
        </Button>
      </TableCell>
    </TableRow>
  );
}
